"use client";

import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import CookiePolicyButton from "@/app/shared-components/cookie-policy-button/cookie-policy-button";
import CountryChangeModal from "@/app/shared-components/country-change-modal/country-change-modal";
import HelpOverLayButton from "@/app/shared-components/help-overlay-button/help-overlay-button-wizard";
import QuestionFooterSection from "@/app/shared-components/question-footer-section";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FloatingChatButton from "@/components/molecules/floating-chat-button/floating-chat-button";
import Logo from "@/components/molecules/logo-component/logo";
import MedicalDashboard from "@/components/organisms/medical-section/medical-dashboard/medical-dashboard";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";

interface ClientComponentProps {
  medicalDashboardContent: any;
  pageName: string;
}

function MedicalDashboardWrapper({
  medicalDashboardContent,
  pageName,
}: Readonly<ClientComponentProps>) {
  const { referenceId } = useSelector((state: RootState) => state.quote);
  const { showLoading, hideLoading } = useLoading();
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");
  const { chatButtonLabelMobile, chatButtonLabelDesktop } =
    medicalDashboardContent;

  const { pageReferrer, pageUrl, pageTitle } = useCustomPageTracking(pageName);

  if (
    typeof window !== "undefined" &&
    window.sessionStorage &&
    sessionStorage.getItem("medical-libary-style-loaded")
  ) {
    sessionStorage.removeItem("medical-libary-style-loaded");
    window.location.reload();
  }

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  const { current, wizard } = useWizard();
  const { productSpecialFeatures } = wizard;
  useUpdateProgressBar(current?.progressBarPercentage ?? -1);

  useEffect(() => {
    if (!referenceId) {
      showLoading();
    } else {
      hideLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId]);

  if (!isReady) return null;

  return (
    <div
      data-testid="medical-dashboard-wrapper-id"
      className=" bg-surface-neutral-option-2 "
    >
      {productSpecialFeatures?.showCountryFooter && <CountryChangeModal />}
      <div className="max-w-[1200px] mx-auto my-0 w-[100%] relative min-h-[calc(100vh-12px)] md:pb-0 pb-spacing-5xl ">
        <main className="md:pt-spacing-xl lg:pt-spacing-xxl md:pb-spacing-xxl pt-spacing-m pb-[120px] ">
          <div
            className=" md:gap-spacing-l gap-spacing-m xl:mx-auto lg:mx-[72px] 
      md:mx-[48px] mx-[24px] relative"
          >
            <div className="md:bg-dark-surface-accent-1-primary rounded-radius-xxl ">
              <div className=" flex flex-col h-full">
                <div className="text-surface-neutral-option-2 relative bg-dark-surface-accent-1-primary rounded-tl-2xl">
                  <HelpOverLayButton />
                  <Logo
                    color="white"
                    heightClass="h-[80px]"
                    size="xl"
                    showondesktop={isdesktop.toString()}
                  />
                </div>
                <MedicalDashboard
                  medicalDashboardContent={medicalDashboardContent}
                />
              </div>
            </div>
          </div>
          <QuestionFooterSection />
          {productSpecialFeatures?.useNewHelpOverlay && <CookiePolicyButton />}
        </main>
      </div>
      <FloatingChatButton
        chatButtonLabelMobile={chatButtonLabelMobile}
        chatButtonLabelDesktop={chatButtonLabelDesktop}
        show={productSpecialFeatures?.showNewChatButton}
      />
    </div>
  );
}

export default MedicalDashboardWrapper;
